/**
 * Deterministic presentation rules for persisted and legacy timeline items.
 *
 * A persisted media type describes what was published. These modes describe
 * how that item should be presented. Keep this module framework-independent so
 * the overlay and composer preview can share the same decision.
 */

export const PRESENTATION_MODES = Object.freeze({
  STORY: "story",
  GALLERY: "gallery",
  ARTICLE: "article",
  PLAYER: "player",
  READER: "reader",
  TEXT_STORY: "text-story",
  COMMERCE: "commerce",
  EXTERNAL: "external",
  EMBED: "embed",
});

const SHORT_BODY_MAX_LENGTH = 140;
const VIDEO_URL_RE = /(\.(mp4|webm|mov|m4v))(\?|#|$)/i;
const AUDIO_URL_RE = /(\.(mp3|wav|ogg|m4a|aac|flac|wma))(\?|#|$)/i;
const DOCUMENT_URL_RE = /(\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|md|csv|json))(\?|#|$)/i;

const normalizeType = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeUrl = (value) =>
  typeof value === "string" ? value.trim() : "";

const firstUrl = (...values) => {
  for (const value of values) {
    const url = normalizeUrl(value);
    if (url) return url;
  }
  return "";
};

const toText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => toText(entry).trim())
      .filter(Boolean)
      .join("\n");
  }
  if (typeof value === "object") {
    return toText(value.text || value.value || value.message || "");
  }
  return "";
};

const getEffectiveMediaType = (item) => {
  const declaredType = normalizeType(item?.mediaType);
  if (declaredType && declaredType !== "nft") return declaredType;

  // NFT is presentation metadata, not an authoring format. These optional
  // fields let callers preserve an underlying type when one is available.
  return normalizeType(
    item?.underlyingMediaType ||
      item?.originalMediaType ||
      item?.sourceMediaType,
  );
};

/**
 * Return the authored post body without including a title or per-media caption.
 * New records prefer `description`; `caption` and `itemDescription` preserve
 * compatibility with existing timeline records.
 */
export function getAuthoredBody(item) {
  const candidates = [
    item?.description,
    item?.caption,
    item?.itemDescription,
  ];

  for (const candidate of candidates) {
    const text = toText(candidate).trim();
    if (text) return text.replace(/\r\n?/g, "\n");
  }

  return "";
}

/**
 * Meaningful authored text selects Article regardless of attached media count.
 */
export function hasMeaningfulBody(item) {
  if (getEffectiveMediaType(item) === "post") return true;

  const body = getAuthoredBody(item);
  return body.length > SHORT_BODY_MAX_LENGTH || /\n[^\S\n]*\n/.test(body);
}

const inferVisualType = (url, declaredType) => {
  const type = normalizeType(declaredType);
  if (type === "video" || type.startsWith("video/")) return "video";
  if (
    type === "audio" ||
    type.startsWith("audio/") ||
    type === "document" ||
    type === "documents" ||
    type === "embed"
  ) {
    return "";
  }
  if (VIDEO_URL_RE.test(url)) return "video";
  if (AUDIO_URL_RE.test(url) || DOCUMENT_URL_RE.test(url)) return "";
  return "image";
};

/**
 * Normalize the image/video entries that participate in Story/Gallery mode.
 *
 * The returned records are deduplicated and never include generated video
 * posters, audio files, or documents. For a standalone `video` record,
 * `images[]` is treated as poster/custom-cover metadata and the video counts as
 * one visual item.
 */
export function getVisualMedia(item) {
  if (!item || typeof item !== "object") return [];

  const mediaType = getEffectiveMediaType(item);
  const images = Array.isArray(item.images) ? item.images : [];
  const mediaTypes = Array.isArray(item.mediaTypes) ? item.mediaTypes : [];
  const posterUrls = new Set(
    (Array.isArray(item.mediaThumbnails) ? item.mediaThumbnails : [])
      .map((url) => normalizeUrl(url))
      .filter(Boolean),
  );
  const visualMedia = [];
  const seen = new Set();

  const addVisual = (candidate, declaredType, extra = {}) => {
    const url = normalizeUrl(candidate);
    if (!url || seen.has(url) || posterUrls.has(url)) return;

    const type = inferVisualType(url, declaredType);
    if (!type) return;

    seen.add(url);
    visualMedia.push({ type, url, ...extra });
  };

  if (mediaType === "video") {
    const videoUrl = firstUrl(
      item.mediaUrl,
      item.videoUrl,
      item.processedVideoUrl,
      item.originalVideoUrl,
      images.find((url, index) => {
        const declaredType = normalizeType(mediaTypes[index]);
        return declaredType === "video" || VIDEO_URL_RE.test(normalizeUrl(url));
      }),
    );

    if (videoUrl) {
      addVisual(videoUrl, "video", {
        poster:
          firstUrl(
            Array.isArray(item.mediaThumbnails)
              ? item.mediaThumbnails.find(Boolean)
              : "",
            images.find((url) => {
              const normalized = normalizeUrl(url);
              return normalized && !VIDEO_URL_RE.test(normalized);
            }),
          ) || null,
      });
      return visualMedia;
    }

    // Malformed legacy video records may expose only a poster. Keep their
    // presentation stable as a one-item Story rather than an empty result.
    const legacyVisual = images.find((url) => normalizeUrl(url));
    if (legacyVisual) addVisual(legacyVisual, "image");
    return visualMedia;
  }

  images.forEach((url, index) => {
    addVisual(url, mediaTypes[index]);
  });

  addVisual(item.mediaUrl, mediaType);

  // Legacy records can use a dedicated video field without mirroring it into
  // `mediaUrl` or `images[]`. Processed/original URLs are alternate versions
  // of the same video, so only the first available one is added.
  const legacyVideoUrl = firstUrl(
    item.videoUrl,
    item.processedVideoUrl,
    item.originalVideoUrl,
  );
  addVisual(legacyVideoUrl, "video");

  if (visualMedia.length === 0) {
    addVisual(firstUrl(item.image, item.imageUrl), "image");
  }

  return visualMedia;
}

export function getVisualMediaCount(item) {
  return getVisualMedia(item).length;
}

/**
 * Mobile Story media covers only after a valid portrait/square ratio is known.
 * Desktop, landscape, and unknown dimensions remain contained.
 */
export function shouldCoverStoryMedia(aspectRatio, isMobileViewport) {
  return (
    !!isMobileViewport &&
    Number.isFinite(aspectRatio) &&
    aspectRatio > 0 &&
    aspectRatio <= 1
  );
}

/**
 * Resolve one mutually exclusive presentation mode.
 */
export function resolvePresentationMode(item) {
  const type = normalizeType(item?.type);
  const mediaType = getEffectiveMediaType(item);
  const declaredMediaType = normalizeType(item?.mediaType);
  const canInferMissingUnderlyingType =
    !declaredMediaType || declaredMediaType === "nft";
  const hasVideoSource = !!firstUrl(
    item?.mediaUrl,
    item?.videoUrl,
    item?.processedVideoUrl,
    item?.originalVideoUrl,
  );

  if (
    type === "product" ||
    type === "service" ||
    mediaType === "product" ||
    mediaType === "service"
  ) {
    return PRESENTATION_MODES.COMMERCE;
  }
  if (
    mediaType === "audio" ||
    (canInferMissingUnderlyingType &&
      !!normalizeUrl(item?.audioUrl) &&
      !hasVideoSource)
  ) {
    return PRESENTATION_MODES.PLAYER;
  }
  if (
    mediaType === "documents" ||
    mediaType === "document" ||
    (canInferMissingUnderlyingType &&
      Array.isArray(item?.documentUrls) &&
      item.documentUrls.length > 0)
  ) {
    return PRESENTATION_MODES.READER;
  }
  if (type === "multimedia" || mediaType === "multimedia") {
    return PRESENTATION_MODES.EXTERNAL;
  }
  if (mediaType === "embed" || !!item?.embedHtml) {
    return PRESENTATION_MODES.EMBED;
  }
  if (mediaType === "poster" || mediaType === "tweet") {
    return PRESENTATION_MODES.TEXT_STORY;
  }

  if (hasMeaningfulBody(item)) return PRESENTATION_MODES.ARTICLE;

  const visualMediaCount = getVisualMediaCount(item);
  if (visualMediaCount > 1) return PRESENTATION_MODES.GALLERY;
  if (
    visualMediaCount === 0 &&
    (mediaType === "text" ||
      !!getAuthoredBody(item) ||
      !!toText(item?.itemName || item?.title).trim())
  ) {
    return PRESENTATION_MODES.TEXT_STORY;
  }
  return PRESENTATION_MODES.STORY;
}
